import React from 'react';
import {
  Grid,
  Card,
  CardHeader,
  Divider,
  CardContent,
  MenuItem,
  Button,
  CardActions,
  Checkbox,
  FormControlLabel,
  Typography,
  TextField,
  makeStyles,
} from '@material-ui/core';
import { AutoFocusedForm, required, SelectField } from 'components';
import { Field } from 'react-final-form';
import { AddVersionValues, useVersionForm, validateVersionPart } from './useVersionForm';
import { ClientApplication } from 'schema/serverTypes';
import { Alert } from '@material-ui/lab';
import { compareVersions, formatVersion, parseVersion, platformLabel } from '../../versionUtils';

const useStyles = makeStyles((theme) => ({
  actions: {
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  preview: {
    marginTop: theme.spacing(2),
  },
}));

export interface CreateVersionFormProps {
  onClose: () => void;
}

export const CreateVersionForm = ({ onClose }: CreateVersionFormProps) => {
  const styles = useStyles();
  const { onSubmit, initialValues, summaries, created, presetClient } = useVersionForm(onClose);

  return (
    <AutoFocusedForm<AddVersionValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{ submitError: true, hasValidationErrors: true }}
      render={({ handleSubmit, hasValidationErrors, submitting, submitError, values }) => {
        const draft = parseVersion(`${values.major}.${values.minor}.${values.patch}`);
        const summary = summaries.data?.clientVersionSummaries.find(
          (item) => item.client === values.client
        );
        const ready = !summaries.loading && !summaries.error && !!summary;
        const latest =
          draft && summary
            ? !summary.latestVersion || compareVersions(draft, summary.latestVersion) > 0
              ? draft
              : summary.latestVersion
            : null;
        const boundary = summary
          ? draft &&
            values.mandatory &&
            (!summary.mandatoryVersion || compareVersions(draft, summary.mandatoryVersion) > 0)
            ? draft
            : summary.mandatoryVersion
          : null;
        const disabled = submitting || created;

        return (
          <form onSubmit={handleSubmit} noValidate>
            <Card>
              <CardHeader
                title={
                  presetClient
                    ? `Добавление версии — ${platformLabel(presetClient)}`
                    : 'Добавление версии'
                }
                titleTypographyProps={{ id: 'create-version-title', component: 'h2' }}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  {!presetClient && (
                    <Grid item xs={12}>
                      <Field
                        name="client"
                        component={SelectField}
                        label="Платформа *"
                        validate={required}
                        disabled={disabled}
                      >
                        <MenuItem value={ClientApplication.Android}>
                          {platformLabel(ClientApplication.Android)}
                        </MenuItem>
                        <MenuItem value={ClientApplication.Ios}>
                          {platformLabel(ClientApplication.Ios)}
                        </MenuItem>
                      </Field>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      Номер версии: три целых неотрицательных числа, например 2.10.3.
                    </Typography>
                  </Grid>
                  {[
                    { name: 'major', label: 'Основная (major)' },
                    { name: 'minor', label: 'Дополнительная (minor)' },
                    { name: 'patch', label: 'Исправление (patch)' },
                  ].map((part) => (
                    <Grid item xs={12} sm={4} key={part.name}>
                      <Field
                        name={part.name}
                        validate={validateVersionPart}
                        render={({ input, meta }) => (
                          <TextField
                            {...input}
                            id={`new-version-${part.name}`}
                            fullWidth
                            error={!!meta.error && meta.touched}
                            helperText={meta.touched ? meta.error : undefined}
                            variant="outlined"
                            label={`${part.label} *`}
                            disabled={disabled}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                          />
                        )}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Field
                      name="mandatory"
                      type="checkbox"
                      render={({ input }) => (
                        <FormControlLabel
                          label="Требовать обновление с более старых версий"
                          control={<Checkbox {...input} disabled={disabled} />}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <div className={styles.preview} aria-live="polite">
                  {created ? (
                    <Alert severity="success">
                      Версия добавлена. Осталось обновить данные экрана.
                    </Alert>
                  ) : summaries.loading ? (
                    <Typography>Загружаются правила платформы…</Typography>
                  ) : summaries.error || !summary ? (
                    <Alert
                      severity="error"
                      action={
                        <Button
                          color="inherit"
                          onClick={() => {
                            summaries.refetch().catch(() => undefined);
                          }}
                        >
                          Повторить
                        </Button>
                      }
                    >
                      Не удалось загрузить правила платформы. Предпросмотр и добавление недоступны.
                    </Alert>
                  ) : draft && latest ? (
                    <Alert severity="info">
                      <Typography variant="subtitle2">
                        После добавления для {platformLabel(values.client)}
                      </Typography>
                      <Typography variant="body2">
                        Последняя зарегистрированная версия: {formatVersion(latest)}.
                      </Typography>
                      {boundary ? (
                        <>
                          <Typography variant="body2">
                            Обновление обязательно для версий ниже {formatVersion(boundary)}.
                          </Typography>
                          {compareVersions(boundary, latest) < 0 && (
                            <Typography variant="body2">
                              Для версий от {formatVersion(boundary)} до {formatVersion(latest)} (не
                              включая {formatVersion(latest)}) обновление необязательное.
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2">
                          Обязательных обновлений нет. Для версий ниже {formatVersion(latest)}{' '}
                          обновление необязательное.
                        </Typography>
                      )}
                      <Typography variant="body2">
                        Для версии {formatVersion(latest)} и более новых обновление не требуется.
                      </Typography>
                      {!values.mandatory && summary.mandatoryVersion && (
                        <Typography variant="body2">
                          Снятый флажок не отменяет существующую границу обязательного обновления.
                        </Typography>
                      )}
                      {summary.latestVersion &&
                        compareVersions(draft, summary.latestVersion) < 0 && (
                          <Typography variant="body2">
                            Добавляется более старая версия: последняя зарегистрированная версия не
                            изменится.
                          </Typography>
                        )}
                    </Alert>
                  ) : (
                    <Typography variant="body2">
                      Введите корректный номер версии, чтобы увидеть итоговое правило.
                    </Typography>
                  )}
                </div>
                {submitError && (
                  <Alert severity="error" className={styles.preview}>
                    {submitError}
                  </Alert>
                )}
              </CardContent>
              <CardActions className={styles.actions}>
                <Button
                  color="primary"
                  disabled={submitting || (!created && (hasValidationErrors || !draft || !ready))}
                  size="medium"
                  type="submit"
                  variant="contained"
                >
                  {submitting ? 'Сохранение…' : created ? 'Обновить данные' : 'Добавить'}
                </Button>
                <Button onClick={onClose} disabled={submitting} size="medium" type="button">
                  {created ? 'Закрыть' : 'Отменить'}
                </Button>
              </CardActions>
            </Card>
          </form>
        );
      }}
    />
  );
};
